import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { NavigationBar } from './components/organisms/navigation-bar/navigation-bar';
import { PianoRoll } from './components/organisms/piano-roll/piano-roll';
import { OscillatorPage } from './components/pages/oscillator-page/oscillator-page';
import { CreditsPage } from './components/pages/credits-page/credits-page';
import { ComposerPage } from './components/pages/composer-page/composer-page';
import { ProjectsPage } from './components/pages/projects-page/projects-page';
import { EVENTS } from './events/events';
import { triggerAttackAll, triggerReleaseAll } from './synth';
import { Tab } from './types';
import { useLocalStorage } from './hooks/use-local-storage';
import * as Tone from "tone";
import { initMidi, MidiStatus } from './midi';
import { DEFAULT_PROJECTS } from './components/pages/projects-page/default-projects';
import { loadShare } from './share';

// Seed Twinkle Twinkle Little Star on first ever visit so new users hear something immediately
if (!localStorage.getItem('synthkitty:initialized')) {
  const twinkle = DEFAULT_PROJECTS.find(p => p.id === 'default-twinkle');
  if (twinkle) {
    localStorage.setItem('synthkitty:oscillators', JSON.stringify(twinkle.oscillators));
    localStorage.setItem('synthkitty:bpm', JSON.stringify(twinkle.bpm));
    localStorage.setItem('synthkitty:daw-measures', JSON.stringify(twinkle.measures));
    localStorage.setItem('synthkitty:daw-notes', JSON.stringify(twinkle.notes));
    localStorage.setItem('synthkitty:daw-loop', JSON.stringify(twinkle.loop));
  }
  localStorage.setItem('synthkitty:initialized', '1');
}

function App() {

  const [shake, setShake] = useState(false);
  const [activeTab, setActiveTab] = useLocalStorage<Tab>('synthkitty:activeTab', 'composer');

  const [midiStatus, setMidiStatus] = useState<MidiStatus>('pending');
  const [sharedProjectName, setSharedProjectName] = useState<string | null>(null);
  const activeNotes = useRef(new Set<string>());
  const activeComposerNotes = useRef(0);

  useEffect(() => {
    const handleKeyDown = (event: Event) => {
      const note = (event as CustomEvent).detail;
      triggerAttackAll(note, Tone.now());
      activeNotes.current.add(note);
      setShake(true);
    };

    const handleKeyUp = (event: Event) => {
      const note = (event as CustomEvent).detail;
      triggerReleaseAll(note, Tone.now());
      activeNotes.current.delete(note);
      setShake(activeNotes.current.size > 0 || activeComposerNotes.current > 0);
    };

    const handleComposerNoteOn = () => {
      activeComposerNotes.current++;
      setShake(true);
    };

    const handleComposerNoteOff = () => {
      activeComposerNotes.current = Math.max(0, activeComposerNotes.current - 1);
      setShake(activeNotes.current.size > 0 || activeComposerNotes.current > 0);
    };

    const handleComposerStopped = () => {
      activeComposerNotes.current = 0;
      setShake(activeNotes.current.size > 0);
    };

    window.addEventListener(EVENTS.PIANO_KEY_DOWN, handleKeyDown);
    window.addEventListener(EVENTS.PIANO_KEY_UP, handleKeyUp);
    window.addEventListener(EVENTS.COMPOSER_NOTE_ON, handleComposerNoteOn);
    window.addEventListener(EVENTS.COMPOSER_NOTE_OFF, handleComposerNoteOff);
    window.addEventListener(EVENTS.COMPOSER_STOPPED, handleComposerStopped);

    return () => {
      window.removeEventListener(EVENTS.PIANO_KEY_DOWN, handleKeyDown);
      window.removeEventListener(EVENTS.PIANO_KEY_UP, handleKeyUp);
      window.removeEventListener(EVENTS.COMPOSER_NOTE_ON, handleComposerNoteOn);
      window.removeEventListener(EVENTS.COMPOSER_NOTE_OFF, handleComposerNoteOff);
      window.removeEventListener(EVENTS.COMPOSER_STOPPED, handleComposerStopped);
    };
  }, []);

  useEffect(() => {
    return initMidi(setMidiStatus);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareId = params.get('share');
    if (!shareId) return;
    // Remove param from URL immediately so refreshes don't re-trigger
    window.history.replaceState({}, '', window.location.pathname);
    loadShare(shareId).then(project => {
      if (!project) return;
      const broadcast = (key: string, value: unknown) => {
        const serialized = JSON.stringify(value);
        localStorage.setItem(key, serialized);
        window.dispatchEvent(
          new CustomEvent('synthkitty:storage-update', { detail: { key, value: serialized } }),
        );
      };
      broadcast('synthkitty:oscillators', project.oscillators);
      broadcast('synthkitty:bpm', project.bpm);
      broadcast('synthkitty:daw-measures', project.measures);
      broadcast('synthkitty:daw-notes', project.notes);
      broadcast('synthkitty:daw-loop', project.loop);
      setActiveTab('composer');
      setSharedProjectName(project.name);
      setTimeout(() => setSharedProjectName(null), 4000);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) {
      link.href = shake ? '/logo-sing.png' : '/logo.png';
    }
  }, [shake]);

  return <div className="sk-app">
    <header>
      <div className="sk-logo-container">
        <img src={shake ? "logo-sing.png" : "logo.png"} alt="Logo" className={shake ? "shaking" : ""} />
      </div>
      <h1 className="pixel-font">SYNTHKITTY</h1>
      <p className="pixel-font">猫</p>
    </header>

    <main>
      <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} midiStatus={midiStatus} />

      {sharedProjectName && (
        <div className="sk-share-banner pixel-font">
          Loaded shared project: {sharedProjectName}
        </div>
      )}

      <div className="sk-tab-content">
        <div role="tabpanel" id="tabpanel-oscillators" aria-labelledby="tab-oscillators" aria-hidden={activeTab !== 'oscillators'} className={`sk-tab-panel${activeTab === 'oscillators' ? ' sk-tab-panel--active' : ''}`}><OscillatorPage /></div>
        <div role="tabpanel" id="tabpanel-composer"    aria-labelledby="tab-composer"    aria-hidden={activeTab !== 'composer'}    className={`sk-tab-panel${activeTab === 'composer'    ? ' sk-tab-panel--active' : ''}`}><ComposerPage /></div>
        <div role="tabpanel" id="tabpanel-projects"    aria-labelledby="tab-projects"    aria-hidden={activeTab !== 'projects'}    className={`sk-tab-panel${activeTab === 'projects'    ? ' sk-tab-panel--active' : ''}`}><ProjectsPage /></div>
        <div role="tabpanel" id="tabpanel-credits"     aria-labelledby="tab-credits"     aria-hidden={activeTab !== 'credits'}     className={`sk-tab-panel${activeTab === 'credits'     ? ' sk-tab-panel--active' : ''}`}><CreditsPage /></div>
      </div>

      <PianoRoll />
    </main>

    <footer className="sk-footer">
      <span>&copy; {new Date().getFullYear()} Equites Digital</span>
      <span className="sk-footer-sep">·</span>
      <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
    </footer>

  </div>
}

export default App;
